'use client';
import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useEffect,
} from 'react';
import { useProperties } from './properties-context';
import {
	FetchedTrialsList,
	TrialsContextType,
	WebsiteTrial as Trial,
} from '@/types/trial';

import useSWR from 'swr';
import { useUser } from './user-context';

export const defaultValues: Trial[] = [];

const TrialsContext = createContext<TrialsContextType | undefined>(undefined);

const fetcher = async (
	url: string,
	payload: string
): Promise<FetchedTrialsList> => {
	const res = await fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: payload,
	});
	if (!res.ok) throw new Error('Failed to fetch trials');
	return res.json();
};

export const TrialsProvider = ({ children }: { children: ReactNode }) => {
	const { properties } = useProperties();
	const { user } = useUser();

	const [trials, setTrials] = useState<Trial[]>(defaultValues);
	const [trialsCount, setTrialsCount] = useState<number>(0);
	const [hasSponsor, setHasSponsor] = useState<boolean>(false);

	const swrKey = [
		'/api/trials',
		JSON.stringify({
			properties,
			user: properties.markedTrials?.followedByYou
				? { follow: user.follow, hide: user.hide }
				: { follow: [], hide: user.hide },
		}),
	];

	const { data, error, isLoading } = useSWR(swrKey, ([url, json]) =>
		fetcher(url, json)
	);

	useEffect(() => {
		if (!data) {
			setTrials([]);
			setTrialsCount(0);
			setHasSponsor(false);
			return;
		}

		const { trials: fetched, count, hasSponsor } = data;

		setTrialsCount(count ?? 0);
		setTrials(fetched);
		setHasSponsor(hasSponsor);
	}, [data]);

	return (
		<TrialsContext.Provider
			value={{
				trials,
				trialsCount,
				isLoading,
				error,
				hasSponsor,
			}}
		>
			{children}
		</TrialsContext.Provider>
	);
};

export const useTrials = () => {
	const context = useContext(TrialsContext);
	if (!context) {
		throw new Error('useTrials must be used within a TrialsProvider');
	}
	return context;
};
